import React from "react";
import { CircleAlert, CircleCheck, Search } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PRIMARY_COLOR } from "@/constants/constants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  getRtoPackageTracking,
  RtoPackageTrackingData,
} from "@/services/rto-package.service";
import { Toaster } from "@/components/ui/toaster";

const formatDate = (date?: string) => {
  if (!date) return "-";
  const value = new Date(date);
  if (Number.isNaN(value.getTime())) return "-";
  return value.toLocaleString("fr-FR");
};

export default function RtoPackage() {
  const [trackingCode, setTrackingCode] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [trackingData, setTrackingData] =
    React.useState<RtoPackageTrackingData | null>(null);

  const isTreated = trackingData?.status === "treated";

  const handleSearch = async () => {
    if (!trackingCode.trim()) return;

    setIsLoading(true);
    setErrorMessage("");

    const response = await getRtoPackageTracking(trackingCode.trim());
    setIsLoading(false);

    if (!response.success || !response.data) {
      setTrackingData(null);
      setErrorMessage(
        response.error || "Impossible de recuperer les informations du colis."
      );
      return;
    }

    setTrackingData(response.data);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const renderInfoItem = (label: string, value: React.ReactNode) => (
    <div className="grid grid-cols-1 gap-1 border-b border-slate-100 px-3 py-2 sm:grid-cols-[180px_1fr] sm:items-center">
      <p className="text-xs font-semibold text-slate-700">{label}</p>
      <p className="text-xs text-slate-600 break-words">{value || "-"}</p>
    </div>
  );

  return (
    <>
      <ScrollArea className="flex-grow sm:px-2">
        <div className="space-y-3 sm:px-2">
          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle
                className="text-xl sm:text-2xl font-bold"
                style={{ color: PRIMARY_COLOR }}
              >
                Suivi colis RTO
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-3">
              <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
                <Input
                  placeholder="Entrer le tracking code"
                  value={trackingCode}
                  onChange={(e) => setTrackingCode(e.target.value.trim())}
                  onKeyDown={handleKeyDown}
                  className="h-10"
                />
                <Button
                  className="h-10 px-4 w-full sm:w-auto"
                  onClick={handleSearch}
                  disabled={isLoading}
                >
                  <Search className="h-4 w-4 mr-1" />
                  {isLoading ? "Recherche..." : "Rechercher"}
                </Button>
              </div>

              {errorMessage && (
                <div className="w-full bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm text-red-700 flex items-start gap-2">
                  <CircleAlert className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{errorMessage}</span>
                </div>
              )}

              {trackingData && (
                <div
                  className={`w-full rounded-md px-3 py-2 text-sm flex items-start gap-2 ${
                    isTreated
                      ? "bg-emerald-50 border border-emerald-200 text-emerald-700"
                      : "bg-amber-50 border border-amber-200 text-amber-700"
                  }`}
                >
                  {isTreated ? (
                    <CircleCheck className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  ) : (
                    <CircleAlert className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  )}
                  <span>
                    {isTreated
                      ? "Colis traite avec succes."
                      : "Ce colis est en attente de traitement."}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {trackingData && (
            <div className="space-y-3">
              <div className={`grid gap-3 ${isTreated ? "md:grid-cols-2" : "md:grid-cols-1"}`}>
                <Card className="shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Informations colis</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 px-0">
                    <div>
                      {renderInfoItem("Tracking Code", trackingData.trackingCode)}
                      {renderInfoItem("Reference commande", trackingData.refCommande)}
                      {trackingData.tourneeId !== undefined &&
                        renderInfoItem("Tournee ID", trackingData.tourneeId)}
                      {trackingData.demandeRepriseId !== undefined &&
                        renderInfoItem(
                          "Demande Reprise ID",
                          trackingData.demandeRepriseId
                        )}
                      {renderInfoItem("Point de drop", trackingData.hubName)}
                      {renderInfoItem("Colis au Hub", trackingData.onHub)}
                      {!isTreated && renderInfoItem("Vendeur", trackingData.sellerName)}
                      {!isTreated && renderInfoItem("Cree par", trackingData.createdBy)}
                      {!isTreated &&
                        renderInfoItem(
                          "Date creation",
                          formatDate(trackingData.createdAt)
                        )}
                    </div>
                  </CardContent>
                </Card>

                {isTreated && (
                  <Card className="shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Vendeur & Traitement</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 px-0">
                      <div>
                        {renderInfoItem("Vendeur", trackingData.sellerName)}
                        {renderInfoItem("Cree par", trackingData.createdBy)}
                        {renderInfoItem(
                          "Date creation",
                          formatDate(trackingData.createdAt)
                        )}
                        {renderInfoItem("Traite par", trackingData.treatedBy)}
                        {renderInfoItem(
                          "Date traitement",
                          formatDate(trackingData.dateTreated)
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {isTreated && (
                <Card className="shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Details des produits</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 px-0">
                    <div className="hidden sm:block overflow-x-auto">
                      <table className="w-full border-collapse min-w-[640px]">
                        <thead className="bg-slate-900 text-white">
                          <tr>
                            <th className="text-left text-xs px-3 py-2">Image</th>
                            <th className="text-left text-xs px-3 py-2">SKU</th>
                            <th className="text-left text-xs px-3 py-2">Libelle</th>
                            <th className="text-left text-xs px-3 py-2">Quantite</th>
                          </tr>
                        </thead>
                        <tbody>
                          {trackingData.details?.map((detail) => (
                            <tr key={detail.sku} className="border-b border-slate-100">
                              <td className="px-3 py-2">
                                <img
                                  src={detail.image}
                                  alt={detail.title}
                                  className="h-10 w-10 object-cover rounded"
                                />
                              </td>
                              <td className="text-xs text-slate-600 px-3 py-2">{detail.sku}</td>
                              <td className="text-xs text-slate-600 px-3 py-2">{detail.title}</td>
                              <td className="text-xs text-slate-600 px-3 py-2">{detail.qte}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="space-y-2 sm:hidden px-3">
                      {trackingData.details?.map((detail) => (
                        <div
                          key={detail.sku}
                          className="border border-slate-200 rounded-md p-2 flex gap-2"
                        >
                          <img
                            src={detail.image}
                            alt={detail.title}
                            className="h-12 w-12 object-cover rounded flex-shrink-0"
                          />
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-slate-700 break-words">
                              {detail.title}
                            </p>
                            <p className="text-xs text-slate-600">SKU: {detail.sku}</p>
                            <p className="text-xs text-slate-600">Quantite: {detail.qte}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          {!trackingData && !errorMessage && (
            <Card className="shadow-sm">
              <CardContent className="p-4 text-sm text-slate-500">
                Recherchez un colis pour afficher les details de suivi RTO.
              </CardContent>
            </Card>
          )}
        </div>
      </ScrollArea>
      <Toaster />
    </>
  );
}
